import React from 'react';
import { SectionReveal } from './ui/SectionReveal';
import { Rocket, Megaphone, Users, DollarSign, Award } from 'lucide-react';
export function Services() {
  const services = [{
    icon: <Rocket className="w-6 h-6 text-accent" data-id="element-190" />,
    title: 'YC & Pitch Coaching',
    tagline: 'Get into the room.',
    description: "I'll help you craft a pitch that actually gets a 'yes' from YC, top-tier VCs, or strategic partners — built on what I learned applying (and getting in W21).",
    outcomes: ['YC application review (line-by-line)', 'Mock interviews with real YC partner questions', 'Pitch deck teardown & rebuild', "Storytelling that doesn't sound rehearsed"]
  }, {
    icon: <Megaphone className="w-6 h-6 text-accent" data-id="element-191" />,
    title: 'Press & Media Features',
    tagline: 'Get on the page.',
    description: "Featured in TechCrunch, Forbes, Fortune, and Entrepreneur — I'll show you how to actually land coverage without hiring a $20k/mo PR agency.",
    outcomes: ['Build a journalist hit list that converts', 'Angles editors actually want to publish', 'Write op-eds that get past the inbox', 'Forbes Tech Council & contributor playbook']
  }, {
    icon: <Users className="w-6 h-6 text-accent" data-id="element-192" />,
    title: 'Engineering Leadership',
    tagline: 'Get the team shipping.',
    description: "10+ years building Waydev across 500+ engineering orgs. I help CTOs and VPEs turn engineering into the company's sharpest competitive edge.",
    outcomes: ["DORA metrics that don't feel like surveillance", 'Scale from 20 → 100+ engineers (without chaos)', 'Engineering ladders that work', 'Org diagnostics & 90-day fix plans']
  }, {
    icon: <DollarSign className="w-6 h-6 text-accent" data-id="element-193" />,
    title: 'Fundraising Strategy',
    tagline: 'Get the money.',
    description: "Raised multiple rounds for Waydev. I'll help you build investor momentum, run a tight process, and close a round on your terms — not theirs.",
    outcomes: ['Investor target list & warm intro strategy', 'Data room that closes (not stalls) deals', 'Term sheet negotiation prep', 'Manage FOMO and timing like a pro']
  }];
  return <section id="services" className="py-24 md:py-32" data-id="element-194">
      <div className="container mx-auto px-6 md:px-12" data-id="element-195">
        <SectionReveal data-id="element-196">
          <div className="max-w-2xl mb-16" data-id="element-197">
            <h2 className="font-serif text-4xl md:text-5xl mb-6" data-id="element-198">
              How I can help you win.
            </h2>
            <p className="text-lg text-secondary" data-id="element-199">
              Whether you're applying to YC next batch, trying to land a Forbes
              feature, scaling your eng team, or raising your next round — I've
              done it, and I'll help you do it too.
            </p>
          </div>
        </SectionReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-id="element-200">
          {services.map((service, index) => <SectionReveal key={index} delay={index * 0.08} data-id="element-201">
              <div className="h-full p-8 md:p-10 rounded-2xl bg-surface border border-border hover:border-accent/30 transition-colors group" data-id="element-202">
                <div className="flex items-start justify-between mb-6" data-id="element-203">
                  <div className="w-12 h-12 rounded-full bg-base flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-204">
                    {service.icon}
                  </div>
                  <span className="font-serif italic text-accent text-sm" data-id="element-205">
                    {service.tagline}
                  </span>
                </div>
                <h3 className="text-2xl font-serif mb-3 tracking-tight" data-id="element-206">
                  {service.title}
                </h3>
                <p className="text-secondary mb-8 leading-relaxed" data-id="element-207">
                  {service.description}
                </p>
                <ul className="space-y-3" data-id="element-208">
                  {service.outcomes.map((outcome, i) => <li key={i} className="flex items-start gap-3 text-sm text-secondary" data-id="element-209">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent/60 mt-1.5 shrink-0" data-id="element-210" />
                      {outcome}
                    </li>)}
                </ul>
              </div>
            </SectionReveal>)}

          {/* Featured: Gartner & G2 — spans both columns */}
          <SectionReveal delay={services.length * 0.08} className="md:col-span-2" data-id="element-211">
            <div className="relative h-full p-8 md:p-12 rounded-2xl bg-gradient-to-br from-accent/10 via-surface to-surface border border-accent/30 hover:border-accent/60 transition-colors group overflow-hidden" data-id="element-212">
              <div className="absolute -top-16 -right-16 w-64 h-64 bg-accent/10 rounded-full blur-3xl" data-id="element-213" />

              <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-214">
                <div className="md:col-span-7" data-id="element-215">
                  <div className="flex items-center gap-4 mb-6" data-id="element-216">
                    <div className="w-12 h-12 rounded-full bg-base flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-217">
                      <Award className="w-6 h-6 text-accent" data-id="element-218" />
                    </div>
                    <span className="inline-block py-1 px-3 rounded-full bg-accent/20 text-accent text-xs font-medium uppercase tracking-wider" data-id="element-219">
                      Most requested
                    </span>
                  </div>
                  <span className="font-serif italic text-accent text-sm block mb-3" data-id="element-220">
                    Get on the shortlist.
                  </span>
                  <h3 className="text-3xl md:text-4xl font-serif mb-4 tracking-tight" data-id="element-221">
                    Gartner & G2 Leader Strategy
                  </h3>
                  <p className="text-secondary leading-relaxed text-lg mb-6" data-id="element-222">
                    I helped Waydev become a recognized leader in its category.
                    Now I help B2B SaaS founders navigate the analyst &
                    review-site game — turning Gartner mentions and G2 badges
                    into pipeline that closes itself.
                  </p>
                </div>

                <div className="md:col-span-5 md:pl-6 md:border-l md:border-border" data-id="element-223">
                  <p className="text-xs text-muted uppercase tracking-wider font-medium mb-5" data-id="element-224">
                    What you'll walk away with
                  </p>
                  <ul className="space-y-3" data-id="element-225">
                    {['Analyst relations playbook (Gartner, Forrester, IDC)', 'Get into Magic Quadrant, Hype Cycle & Market Guides', 'G2 Leader & High Performer badge strategy', "Review velocity campaigns that don't feel cringe", 'Briefing decks analysts actually remember'].map((outcome, i) => <li key={i} className="flex items-start gap-3 text-sm text-secondary" data-id="element-226">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 shrink-0" data-id="element-227" />
                        {outcome}
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </SectionReveal>
        </div>
      </div>
    </section>;
}